import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, Brain, Clock, Eye, FileText, ListChecks, Loader2, ShieldCheck, X } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useEffect, useMemo, useState } from 'react';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

type TabId = 'esprit' | 'resume' | 'complet' | 'diligence';

interface SuperhumanEFVPModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function escapeHtml(input: string) {
  return input
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function plainTextToHtml(plain: string) {
  const escaped = escapeHtml(plain);
  const lines = escaped.split('\n');

  const out: string[] = [];
  let listMode: 'ul' | 'ol' | null = null;
  const closeList = () => {
    if (!listMode) return;
    out.push(listMode === 'ul' ? '</ul>' : '</ol>');
    listMode = null;
  };

  const openList = (mode: 'ul' | 'ol') => {
    if (listMode === mode) return;
    closeList();
    listMode = mode;
    out.push(mode === 'ul' ? '<ul>' : '<ol>');
  };

  const pushParagraph = (html: string) => {
    closeList();
    out.push(`<p>${html}</p>`);
  };

  const pushHeading = (level: 2 | 3 | 4, text: string) => {
    closeList();
    out.push(`<h${level}>${text}</h${level}>`);
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const t = line.trim();

    if (t === '') {
      closeList();
      out.push('<div class="spacer"></div>');
      continue;
    }

    if (t === '---' || t === '________________________________________') {
      closeList();
      out.push('<hr/>');
      continue;
    }

    // Minimal markdown headings support
    if (t.startsWith('### ')) {
      pushHeading(4, t.slice(4));
      continue;
    }
    if (t.startsWith('## ')) {
      pushHeading(3, t.slice(3));
      continue;
    }
    if (t.startsWith('# ')) {
      pushHeading(2, t.slice(2));
      continue;
    }

    // Unordered list items
    if (t.startsWith('• ') || t.startsWith('- ') || t.startsWith('o ')) {
      openList('ul');
      const item = t.replace(/^•\s+/, '').replace(/^-+\s+/, '').replace(/^o\s+/, '');
      out.push(`<li>${item}</li>`);
      continue;
    }

    // Ordered list items: "1." / "1)" / "1️⃣" etc. We'll support basic "1." and "1)".
    const orderedMatch = t.match(/^(\d+)[\.\)]\s+(.*)$/);
    if (orderedMatch) {
      openList('ol');
      out.push(`<li>${orderedMatch[2]}</li>`);
      continue;
    }

    // Default: keep line breaks within same paragraph if needed (simple strategy)
    pushParagraph(t);
  }

  closeList();
  return out.join('');
}

function Avatar({ src, alt, title }: { src: string; alt: string; title: string }) {
  const [currentSrc, setCurrentSrc] = useState(src);

  return (
    <img
      src={currentSrc}
      alt={alt}
      title={title}
      className="w-8 h-8 rounded-full border-2 border-emerald-400 object-cover"
      onError={() => {
        if (currentSrc !== '/AG-transparent.png') setCurrentSrc('/AG-transparent.png');
      }}
    />
  );
}

export default function SuperhumanEFVPModal({ isOpen, onClose }: SuperhumanEFVPModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('esprit');
  const [resumeMd, setResumeMd] = useState<string>('');
  const [completMd, setCompletMd] = useState<string>('');
  const [diligenceMd, setDiligenceMd] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = lexDates['efvp_superhuman_mail_last_ai_analysis'] ?? null;

  const tabs = useMemo(
    () => [
      { id: 'esprit' as const, label: 'Esprit & objectifs macro', icon: Eye },
      { id: 'resume' as const, label: 'Résumé exécutif', icon: FileText },
      { id: 'complet' as const, label: 'Évaluation complète', icon: BookOpen },
      { id: 'diligence' as const, label: 'Diligence & questions', icon: ListChecks },
    ],
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const [resumeRes, completRes, diligenceRes] = await Promise.all([
          fetch('/knowledge/11-efvp-superhuman-executif.md'),
          fetch('/knowledge/12-efvp-superhuman-complet.md'),
          fetch('/knowledge/13-efvp-superhuman-diligence-questions.md'),
        ]);

        if (!resumeRes.ok) throw new Error('Impossible de charger le résumé exécutif.');
        if (!completRes.ok) throw new Error("Impossible de charger l'évaluation complète.");
        if (!diligenceRes.ok) throw new Error('Impossible de charger le bloc diligence / questions.');

        const [resumeText, completText, diligenceText] = await Promise.all([
          resumeRes.text(),
          completRes.text(),
          diligenceRes.text(),
        ]);

        if (cancelled) return;

        setResumeMd(resumeText);
        setCompletMd(completText);
        setDiligenceMd(diligenceText);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Erreur lors du chargement du contenu.');
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const renderDoc = (md: string) => {
    const html = plainTextToHtml(md);
    return (
      <div
        className={[
          'text-gray-200',
          '[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-6 [&_h2]:mb-2',
          '[&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-white [&_h3]:mt-5 [&_h3]:mb-2',
          '[&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-white [&_h4]:mt-4 [&_h4]:mb-2',
          '[&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3',
          '[&_ul]:ml-6 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:text-sm [&_ul]:leading-relaxed',
          '[&_ol]:ml-6 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:text-sm [&_ol]:leading-relaxed',
          '[&_hr]:my-6 [&_hr]:border-emerald-500/30',
          '[&_.spacer]:h-2',
          '[&_li]:text-gray-200',
        ].join(' ')}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-emerald-900/60 to-slate-900 rounded-2xl w-[92vw] h-[92vh] border border-emerald-500/30 shadow-2xl shadow-emerald-500/10 flex flex-col">
              {/* Header */}
              <div className="relative p-3 border-b border-emerald-500/30 bg-gradient-to-r from-emerald-900/40 to-slate-900/40">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg">
                      <ShieldCheck className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-300 truncate">
                        EFVP — Superhuman Mail (Microsoft 365)
                      </h2>
                      <p className="text-gray-300 text-xs truncate">
                        Approbation outils • Loi 25 • Permissions Graph/Entra • Risques & atténuations
                      </p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/30 border border-purple-400/50 text-xs text-purple-200" title="Disponible dans AI Assistant">
                          <Brain className="w-3.5 h-3.5" />
                          AI
                        </span>
                        {lastAnalysisDate && (
                          <span className="text-gray-400 text-xs flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Avatar src="/VB.png" alt="Vincent Bourassa" title="Vincent Bourassa — Évaluation initiale" />
                      <Avatar src="/FP.png" alt="François Delisle" title="François Delisle — Évaluation / gouvernance" />
                      <Avatar src="/DB.png" alt="Dany Bourassa" title="Dany Bourassa — Demandeur" />
                    </div>
                  </div>

                  <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-gray-300" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-emerald-500/20 bg-slate-900/50 px-6 pt-3 pb-0 overflow-x-auto">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold text-sm transition-all whitespace-nowrap ${
                          active
                            ? 'bg-gradient-to-b from-emerald-600 to-teal-600 text-white shadow-lg'
                            : 'text-gray-300 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loading && (
                  <div className="flex items-center gap-3 text-gray-200">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Chargement des documents…</span>
                  </div>
                )}

                {!loading && loadError && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-100">
                    <p className="font-semibold">Erreur</p>
                    <p className="text-sm mt-1">{loadError}</p>
                    <p className="text-xs mt-2 text-red-200/80">
                      Vérifie que les fichiers existent dans <code className="bg-black/20 px-1.5 py-0.5 rounded">public/knowledge/</code>.
                    </p>
                  </div>
                )}

                {!loading && !loadError && (
                  <AnimatePresence mode="wait">
                    {activeTab === 'esprit' && (
                      <motion.div
                        key="esprit"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="space-y-6"
                      >
                        <div className="bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-emerald-600/20 rounded-xl p-6 border border-emerald-500/30">
                          <h3 className="text-2xl font-bold text-center text-white mb-2">Esprit & objectifs macro</h3>
                          <p className="text-gray-200 text-center text-sm">
                            Le comité Sécurité est la “porte de contrôle” : chaque nouvel outil demandant des accès étendus doit être évalué, documenté et assumé.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <div className="rounded-2xl border border-emerald-500/20 bg-white/5 p-5">
                            <p className="text-sm font-semibold text-white mb-2">Pourquoi on fait une EFVP (Loi 25)</p>
                            <p className="text-sm text-gray-200 leading-relaxed">
                              Pour prouver notre diligence, mesurer l’impact vie privée, et éviter les décisions “à l’instinct” quand un outil demande un
                              consentement tenant-wide.
                            </p>
                          </div>
                          <div className="rounded-2xl border border-emerald-500/20 bg-white/5 p-5">
                            <p className="text-sm font-semibold text-white mb-2">Ce qui fait mal (souvent)</p>
                            <ul className="list-disc ml-5 text-sm text-gray-200 space-y-1.5">
                              <li>Permissions applicatives étendues (Graph / Entra) impossibles à minimiser finement</li>
                              <li>Fournisseur tiers + transferts hors Québec (art. 17)</li>
                              <li>IA/automatisation : exiger une clause explicite “no training”</li>
                            </ul>
                          </div>
                          <div className="rounded-2xl border border-emerald-500/20 bg-white/5 p-5">
                            <p className="text-sm font-semibold text-white mb-2">Décision attendue</p>
                            <ul className="list-disc ml-5 text-sm text-gray-200 space-y-1.5">
                              <li>Autoriser (périmètre limité) avec acceptation formelle du risque</li>
                              <li>Ou refuser tant que le fournisseur ne permet pas un scoping “mail-only” / permissions réduites</li>
                            </ul>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-emerald-500/20 bg-black/20 p-5">
                          <p className="text-sm font-semibold text-white mb-2">Standard BZ (répétabilité)</p>
                          <p className="text-sm text-gray-200 leading-relaxed">
                            On garde toujours un <strong className="text-white">résumé exécutif</strong> + un <strong className="text-white">full détail</strong> +
                            une section <strong className="text-white">questions au fournisseur</strong>. Le but : que le chatbot puisse répondre “sur la coche”
                            et que l’audit trail soit complet.
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {activeTab === 'resume' && (
                      <motion.div
                        key="resume"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="space-y-6"
                      >
                        {renderDoc(resumeMd)}
                      </motion.div>
                    )}

                    {activeTab === 'complet' && (
                      <motion.div
                        key="complet"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="space-y-6"
                      >
                        {renderDoc(completMd)}
                      </motion.div>
                    )}

                    {activeTab === 'diligence' && (
                      <motion.div
                        key="diligence"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="space-y-6"
                      >
                        {renderDoc(diligenceMd)}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

